import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { LocalDate, ZonedDateTime } from '@js-joda/core';
import React from 'react';
import {
  ChatRoomDetailsFragment,
  UserBasicsFragment,
} from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import { AQUAMARINE } from '../../theme';
import { getTimeSince } from '../../utils/date';
import { getMemberName } from '../../utils/getMemberName';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { useChatContext } from '../Chat/Context';
import Avatar, { AvatarTag } from '../reusable/Avatar';
import Text, { TextColor, TextKind } from '../reusable/Text';
import { TextLineClamp } from '../reusable/TextLineClamp';
import { StyledChatListItem, StyledChatlistUnreadIndicator } from './styled';
import { getMessageDisplayText } from './util';

interface ChatListItemProps {
  chat: Omit<ChatRoomDetailsFragment, 'users'>;
  member: UserBasicsFragment;
  onClick: () => void;
  selfId?: string;
}

export const ChatListItem: React.FC<ChatListItemProps> = ({
  chat,
  member,
  selfId,
  onClick,
}) => {
  const { track } = useTracking();
  const { openChat } = useChatContext();

  const handleClick = () => {
    track(EventNames.ListClick, {
      chat_id: chat?.id,
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.ChatList,
    });

    if (chat?.id) {
      openChat({
        member,
        chatId: chat?.id,
      });
      onClick();
    }
  };

  const lastMessage = chat?.messages.edges.at(-1)?.node;
  const hasUnreadMessages = Boolean(
    (chat?.messages.unreadMessageCount || 0) > 0,
  );

  const messageDisplayText = getMessageDisplayText(lastMessage, selfId);

  let avatarTag = AvatarTag.None;
  if (member.isStarred) {
    avatarTag = AvatarTag.Star;
  } else if (member.fitCoach?.id !== selfId) {
    avatarTag = AvatarTag.RoundedSquare;
  }

  return (
    <StyledChatListItem
      onClick={handleClick}
      data-testid="ChatListItem"
      data-itemid={chat?.id}
    >
      <Avatar user={member} size={40} tag={avatarTag} />
      <div>
        <Text
          kind={TextKind.LabelL}
          style={{ marginBottom: '4px' }}
          testId="ChatListItem-MemberName"
        >
          {getMemberName(member)}
        </Text>

        <TextLineClamp $lines={1}>
          <Text
            kind={TextKind.BodyM}
            color={hasUnreadMessages ? TextColor.Primary : TextColor.Secondary}
            bold={hasUnreadMessages}
            testId="ChatListItem-MessagePreview"
          >
            {messageDisplayText}
          </Text>
        </TextLineClamp>

        <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
          {lastMessage?.createdAt &&
            getTimeSince(
              ZonedDateTime.parse(lastMessage.createdAt).toLocalDate(),
              LocalDate.now(),
            )}
        </Text>

        {hasUnreadMessages && (
          <StyledChatlistUnreadIndicator
            data-testid="ChatListItem-unreadIndicator"
            icon={faCircle}
            color={AQUAMARINE}
            fontSize={8}
          />
        )}
      </div>
    </StyledChatListItem>
  );
};
